import { CategoryType, ImageType } from "@/types/baseTypes";
import { PostsGridElementType } from "../components/PostCard/PostCard";
import { IndexedModuleData } from "../types/baseTypes";
import { getAllModuleData } from "./getModulData";
import { WPClient } from "../wp/client/WPClient";
import { PostsSliderModuleType } from "../modules/PostSlider/PostSlider";
import { Post } from "@aleksundshantu/schirdewan-standard-types/client";
import { getDataArrayGeneric } from "./getData";
import { CategoryOverviewPages } from "./getIndexedPostGridData";

interface GetIndexedYourModuleDataProps {
  pageData: Post;
  locale: string;
  defaultLocale: string;
  fallback_options?: "header-img" | "teaser-text";
  fallback_image?: ImageType | false;
}

export const DEFAULT_SETTING: {
  time_control: "off";
  start_date: string;
  end_date: string;
  weekdays: (
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday"
  )[];
} = {
  time_control: "off",
  start_date: "",
  end_date: "",
  weekdays: [],
};

export interface PostSliderData {
  posts: PostsGridElementType[];
  allCategories: CategoryOverviewPages[];
}

export type IndexedPostSlidereData = IndexedModuleData<PostSliderData>;

export const getIndexedPostSliderData = async ({
  pageData,
  locale,
  defaultLocale,
  fallback_options = "teaser-text",
  fallback_image,
}: GetIndexedYourModuleDataProps): Promise<IndexedPostSlidereData> => {
  const moduleData: IndexedPostSlidereData = [];
  const isSingleView = pageData?.type === "blog";
  let modules = getAllModuleData<PostsSliderModuleType>(pageData, ["slider"]);
  if ((!modules || !Object.keys(modules).length) && isSingleView) {
    modules = {
      0: {
        slider: {
          type: "categories",
          categories: false,
          posts: null,
        },
        layout: { posts_per_page: 100 },
        settings: DEFAULT_SETTING,
      } as unknown as PostsSliderModuleType,
    };
  }
  const client = new WPClient({ locale, defaultLocale });

  for (const [key, module] of Object.entries(modules)) {
    const { type, categories, posts: selectedPosts } = module.slider;
    const excludeIds = isSingleView ? [pageData.id] : undefined;
    // Fetch all categories once
    const fetchedCategories = await getDataArrayGeneric<CategoryType[]>(
      "/categories?per_page=100",
      locale,
      defaultLocale,
    );

    const allOverviewPageIds = fetchedCategories.pageData.map(
      (x) => x.meta.overview_page_id,
    );

    let overviewPages: Post[] = [];
    if (allOverviewPageIds.length > 0) {
      const idsString = allOverviewPageIds.filter((id) => id).join(",");
      if (idsString) {
        overviewPages = await getDataArrayGeneric<Post[]>(
          `/pages?include=${idsString}`,
          locale,
          defaultLocale,
        ).then((x) => (Array.isArray(x.pageData) ? x.pageData : []));
      }
    }

    const categoriesWithOverviewLink: CategoryOverviewPages[] =
      fetchedCategories.pageData.map((category) => {
        const overviewPage = overviewPages.find(
          (page) => page.id === category.meta.overview_page_id,
        );
        return {
          slug: category.slug,
          id: category.id,
          link: overviewPage?.link || "",
        };
      });
    const posts = await client.getPosts({
      type: "blog",
      fields: [
        "id",
        "date_gmt",
        "_embedded",
        "acf.header_post.title",
        "acf.header_post.image",
        "acf.header_post.media",
        "link",
        "acf.header_post.excerpt",
        "acf.header_post.content",
        "categories",
      ],
      ...(type === "posts" && {
        includeIds: { ids: selectedPosts || [], keepOrder: true },
      }),
      ...(type === "categories" && {
        includeCategories: [{ category: "categories", ids: categories || [] }],
      }),
      ...(excludeIds && { excludeIds }),
      limit: type === "categories" ? 10 : undefined,
    });

    const fetchedPosts = posts.posts.map((post) => {
      const header = post.acf?.header_post;
      const _categories = post._embedded?.["wp:term"]
        ? post._embedded["wp:term"][0]
        : [];
      return {
        id: post.id,
        date: post.date_gmt ?? null,
        title: header?.title || "",
        // if no preview image, show header image
        // if no header image, show fallback image
        image:
          header?.image ||
          (fallback_options === "header-img"
            ? header.media.image_desktop
              ? header.media.image_desktop
              : fallback_image
                ? fallback_image
                : false
            : false),
        excerpt: header?.excerpt || "",
        link: post.link,
        categories: _categories ? _categories : [],
        content: header?.content || "",
      } as PostsGridElementType;
    });

    moduleData.push({
      index: parseInt(key),
      data: {
        posts: fetchedPosts,
        allCategories: categoriesWithOverviewLink,
      },
    });
  }

  return moduleData;
};
